/** Small shared pieces. Nothing here holds state beyond its own disclosure. */

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';

export function Badge({ kind, children }: { kind: string; children: ReactNode }) {
  return <span className={`badge badge-${kind}`}>{children}</span>;
}

/**
 * Status colour is a judgement, so it lives in one place.
 *
 * `delivered` is the only genuinely good outcome; `sent` is amber because it
 * means we handed the message to SES and have not yet heard back -- showing it
 * green would imply an outcome we do not have.
 */
export function StatusBadge({ status }: { status: string }) {
  const kind =
    status === 'delivered'
      ? 'ok'
      : status === 'bounced' || status === 'complained' || status === 'failed' || status === 'rejected'
        ? 'danger'
        : status === 'sent' || status === 'sending' || status === 'queued' || status === 'delivery_delayed'
          ? 'warn'
          : status === 'scheduled'
            ? 'info'
            : 'muted';
  return <Badge kind={kind}>{status}</Badge>;
}

export function VerificationBadge({ status }: { status: string }) {
  const kind = status === 'verified' ? 'ok' : status === 'failed' ? 'danger' : 'warn';
  return <Badge kind={kind}>{status}</Badge>;
}

export function EnvPill({ environment }: { environment: string }) {
  const live = environment === 'live';
  return (
    <span
      className="env-pill"
      style={{
        background: live ? 'var(--accent-soft)' : 'var(--warn-soft)',
        color: live ? 'var(--accent)' : 'var(--warn)',
      }}
    >
      {environment}
    </span>
  );
}

export function Card({
  title,
  actions,
  children,
  tight,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  if (!title) return <div className="card">{children}</div>;
  return (
    <div className={tight ? 'card card-tight' : 'card'}>
      <div className="card-head">
        <h2>{title}</h2>
        {actions}
      </div>
      {tight ? children : <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

export function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {hint && <div className="tile-hint">{hint}</div>}
    </div>
  );
}

/**
 * A labelled form field with its error state wired up.
 *
 * Exists because the accessibility contract for an errored input is three
 * separate attributes that have to agree with each other -- `aria-invalid` on the
 * control, `aria-describedby` pointing at the message, and an `id` on the message
 * for it to point at -- and any one of them being forgotten silently produces a
 * field that a screen reader announces as valid while sighted users see red. The
 * form mockup in uxui/text-box demonstrates exactly that omission.
 *
 * So the wiring lives here once. A caller supplies an id, a label and optionally
 * an error string, and cannot get the association wrong.
 *
 * The `error` prop takes the message rather than a boolean on purpose: there is no
 * way to mark a field invalid without also saying why, which is the other half of
 * the same rule.
 */
/** A labelled group of related controls -- checkboxes, radios -- where no single
 *  input owns the label. A bare <label> here would point at nothing, so the group
 *  gets a real <fieldset>/<legend>, which is what screen readers announce when
 *  entering the group.
 *
 *  Use Field for one control, FieldGroup for a set of them. */
export function FieldGroup({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string | null;
  hint?: ReactNode;
  children: ReactNode;
}) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <fieldset
      className="field field-group"
      aria-describedby={describedBy || undefined}
    >
      <legend>{label}</legend>
      {children}
      {error && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
    </fieldset>
  );
}

export function Field({
  id,
  label,
  error,
  hint,
  style,
  children,
}: {
  id: string;
  label: string;
  error?: string | null;
  hint?: ReactNode;
  /** Layout only. Some forms lay their fields out in a row, so the wrapper needs
   *  to take flex sizing -- the accessibility wiring is unaffected either way. */
  style?: CSSProperties;
  children: (props: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
    className?: string;
  }) => ReactNode;
}) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  // Both are announced when both exist; describedby takes a space-separated list.
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field" style={style}>
      <label htmlFor={id}>{label}</label>
      {children({
        id,
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { className: 'input-error' } : {}),
      })}
      {/* The message sits under the field, not in a toast: a toast is gone by the
          time someone has finished reading the form. role="alert" so it is
          announced when it appears rather than only when focus reaches it. */}
      {error && (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

export function Alert({
  kind,
  children,
  role,
}: {
  kind: string;
  children: ReactNode;
  /** `status` for something that appears in response to an action a person just
   *  took, so a screen reader announces it. Without it the alert is silent
   *  markup that only sighted users notice, which is the same bug it exists to
   *  fix. Left off for alerts present on first paint — those are read anyway. */
  role?: 'status' | 'alert';
}) {
  return (
    <div className={`alert alert-${kind}`} role={role}>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function PageHead({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="subtitle">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** Absolute timestamps only. "3 hours ago" is useless when correlating with a
 * customer's log line, which is the main thing this dashboard is used for. */
export function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Reveal-and-copy for a value shown exactly once (API key, signing secret). */
/** Copy one value to the clipboard, with feedback.
 *
 *  Extracted rather than written a third time — this is the copy behaviour
 *  SecretReveal already had, and a DNS record needs exactly the same thing.
 *
 *  The clipboard API is unavailable over plain HTTP in some browsers, which is
 *  every local deployment of this dashboard. So the failure path matters: the
 *  value stays selectable text and the button says so, rather than silently
 *  doing nothing and leaving somebody to paste whatever was on their clipboard
 *  before.
 */
export function CopyButton({
  value,
  label = 'Copy',
  title,
}: {
  value: string;
  label?: string;
  title?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 1800);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className="btn btn-sm copy-btn"
      title={title ?? `Copy ${value.length > 40 ? value.slice(0, 40) + '…' : value}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setState('copied');
        } catch {
          setState('failed');
        }
      }}
    >
      {/* aria-live so a screen reader hears the outcome; the label alone
          changing is a visual-only signal. */}
      <span aria-live="polite">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select it' : label}
      </span>
    </button>
  );
}

export function SecretReveal({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      {/* Not a <label>: the value below is a div, not a form control, so a label
          here would point at nothing. */}
      <span className="field-label">{label}</span>
      <div className="secret">{value}</div>
      <div className="row" style={{ marginTop: 7 }}>
        <CopyButton value={value} />
        <span className="hint">Shown once. Store it now — only a hash is kept.</span>
      </div>
    </div>
  );
}

/** A small hook for "load once, show error or data". */
export function useLoader<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    load()
      .then((result) => {
        if (live) {
          setData(result);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
